/**
 * The World agent's record of what it did with a stop (RFC 0348 3e).
 *
 * A report on its own says an agent stopped. The person also needs to know
 * whether the World agent handled it or left it for them, without reading the
 * World agent's reasoning. `decide_stop` writes that as one event on the World
 * agent's own log, and the card under the report reads it.
 *
 * NOT A WRITE TOOL in the machine sense: it touches only the caller's log.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../log/branch-log'
import type { StopVerdict } from '../log/types'
import { type ToolDef, toolArgString } from './project-tools'

const VERDICTS: ReadonlySet<string> = new Set(['answered', 'yours', 'nothing'])

export function stopTools(
  repo: Repo,
  sessionId: string | undefined,
): Record<string, ToolDef> {
  if (!sessionId) return {}
  return {
    decide_stop: {
      description:
        'Record what you did about one stopped session, once per stop report. ' +
        'verdict "answered": you replied to it (send_message or ' +
        'answer_question first), and note is what you said. "yours": the ' +
        "call is the person's, so you left it for them; note says what they " +
        'need to decide. "nothing": it finished cleanly and nothing is owed. ' +
        'The person sees this under the report, so write the note for them.',
      inputSchema: {
        type: 'object',
        properties: {
          sessionId: {
            type: 'string',
            description: 'The session that stopped, from the report.',
          },
          runId: {
            type: 'string',
            description:
              'The run that stopped, from the report ("run …"). Names exactly ' +
              'which stop this decides.',
          },
          verdict: {
            type: 'string',
            enum: ['answered', 'yours', 'nothing'],
          },
          note: {
            type: 'string',
            description: 'One line for the person.',
          },
        },
        required: ['sessionId', 'verdict'],
      },
      execute: async (args) => {
        const target = toolArgString(args.sessionId)?.trim() ?? ''
        const verdict = toolArgString(args.verdict)?.trim() ?? ''
        if (!target) throw new Error('sessionId is required')
        if (!VERDICTS.has(verdict)) {
          throw new Error('verdict is "answered", "yours" or "nothing"')
        }
        const runId = toolArgString(args.runId)?.trim() ?? ''
        const note = (toolArgString(args.note) ?? '').trim().slice(0, 280)
        const handle = await repo.find(sessionId as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        new BranchLog(handle as never).appendStopDecided({
          sessionId: target,
          ...(runId ? { runId } : {}),
          verdict: verdict as StopVerdict,
          ...(note ? { note } : {}),
        })
        return verdict === 'yours'
          ? 'Recorded: left for the person. Do not also ask them in this thread; the card shows it.'
          : `Recorded: ${verdict}.`
      },
    },
  }
}
